import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Select,
  Spinner,
  Center,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Alert,
  AlertIcon,
  useToast,
  Input,
  Stack,
  Badge,
  Divider,
} from '@chakra-ui/react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiService from '../../services/ApiService';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'year', label: 'รายปี' },
  { value: 'month', label: 'รายเดือน' },
  { value: 'custom', label: 'กำหนดช่วงเอง' },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: `เดือน ${i + 1}`,
}));

const formatNumber = (n) => Number(n || 0).toLocaleString('th-TH');

/** ประมาณความกว้างแกน Y ให้ชื่อหมวดหมู่แสดงครบบรรทัดเดียว */
const estimateLabelWidth = (titles) => {
  const longest = (titles || []).reduce((max, title) => {
    const len = String(title || '').length;
    return Math.max(max, len);
  }, 0);
  // ตัวอักษรไทย ~12px ที่ font-size 12
  return Math.min(560, Math.max(120, longest * 12 + 24));
};

const periodLabelThai = (meta) => {
  if (!meta) return 'ทั้งหมด';
  if (meta.period === 'year') return `ปี ${meta.year}`;
  if (meta.period === 'month') return `เดือน ${meta.month}/${meta.year}`;
  if (meta.period === 'custom') {
    const s = meta.start_date ? new Date(meta.start_date).toLocaleDateString('th-TH') : '-';
    const e = meta.end_date ? new Date(meta.end_date).toLocaleDateString('th-TH') : '-';
    return `${s} ถึง ${e}`;
  }
  return 'ทั้งหมด';
};

const StatCard = ({ label, value, hint, accent = 'blue.500' }) => (
  <Box
    bg="white"
    borderRadius="xl"
    p={5}
    shadow="sm"
    borderTop="4px solid"
    borderTopColor={accent}
  >
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="3xl" fontWeight="bold" color="gray.800" lineHeight="1.1">
      {formatNumber(value)}
    </Text>
    {hint && (
      <Text fontSize="xs" color="gray.400" mt={2}>
        {hint}
      </Text>
    )}
  </Box>
);

const StatisticsDashboard = () => {
  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [period, setPeriod] = useState('all');
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState(String(currentMonth));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availableYears, setAvailableYears] = useState([currentYear]);

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [byContent, setByContent] = useState([]);
  const [byPage, setByPage] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (period === 'year' || period === 'month') {
      params.set('year', year);
    }
    if (period === 'month') {
      params.set('month', month);
    }
    if (period === 'custom') {
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
    }
    return params.toString();
  }, [period, year, month, startDate, endDate]);

  const groupBy = period === 'month' || period === 'custom' ? 'day' : 'month';

  const loadYears = useCallback(async () => {
    try {
      const res = await apiService({ method: 'GET', url: '/statistics/available-years' });
      if (res.data?.length) setAvailableYears(res.data);
    } catch {
      // keep default year
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const [summaryRes, trendRes, categoryRes, contentRes, pageRes] = await Promise.all([
        apiService({ method: 'GET', url: `/statistics/summary?${queryString}` }),
        apiService({ method: 'GET', url: `/statistics/trend?${queryString}&group_by=${groupBy}` }),
        apiService({ method: 'GET', url: `/statistics/by-category?${queryString}` }),
        apiService({ method: 'GET', url: `/statistics/by-content?${queryString}&limit=50` }),
        apiService({ method: 'GET', url: `/statistics/by-page?${queryString}&limit=20` }),
      ]);

      setSummary(summaryRes.data);
      setTrend(trendRes.data?.items || []);
      setByCategory(categoryRes.data || []);
      setByContent(contentRes.data || []);
      setByPage(pageRes.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'ไม่สามารถโหลดสถิติได้');
      toast({
        title: 'โหลดสถิติไม่สำเร็จ',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [queryString, groupBy, toast]);

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const meta = useMemo(
    () => ({
      period,
      year: Number(year),
      month: Number(month),
      start_date: startDate || summary?.start_date,
      end_date: endDate || summary?.end_date,
    }),
    [period, year, month, startDate, endDate, summary]
  );

  const categoryChartData = useMemo(
    () => (byCategory || []).slice(0, 8),
    [byCategory]
  );

  const categoryYAxisWidth = useMemo(
    () => estimateLabelWidth(categoryChartData.map((row) => row.category_title)),
    [categoryChartData]
  );

  const categoryChartHeight = useMemo(
    () => Math.max(280, categoryChartData.length * 44),
    [categoryChartData]
  );

  const exportExcel = () => {
    try {
      setIsExporting(true);
      const wb = XLSX.utils.book_new();

      const summarySheet = XLSX.utils.aoa_to_sheet([
        ['รายงานสถิติการเข้าชม'],
        ['ช่วงข้อมูล', periodLabelThai(meta)],
        ['วันที่ออกรายงาน', new Date().toLocaleString('th-TH')],
        [],
        ['ตัวชี้วัด', 'จำนวน'],
        ['ผู้เข้าชม (Unique Visitors)', summary?.unique_visitors || 0],
        ['Page Views', summary?.page_views || 0],
        ['การเปิดเอกสาร', summary?.document_opens || 0],
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'สรุป');

      const trendSheet = XLSX.utils.json_to_sheet(
        (trend || []).map((r) => ({
          ช่วงเวลา: r.period_label,
          ผู้เข้าชม: r.unique_visitors,
          PageViews: r.page_views,
          เปิดเอกสาร: r.document_opens,
        }))
      );
      XLSX.utils.book_append_sheet(wb, trendSheet, 'แนวโน้ม');

      const categorySheet = XLSX.utils.json_to_sheet(
        (byCategory || []).map((r) => ({
          หมวดหมู่: r.category_title,
          ผู้เข้าชม: r.unique_visitors,
          PageViews: r.page_views,
          เปิดเอกสาร: r.document_opens,
        }))
      );
      XLSX.utils.book_append_sheet(wb, categorySheet, 'ตามหมวดหมู่');

      const contentSheet = XLSX.utils.json_to_sheet(
        (byContent || []).map((r) => ({
          เอกสาร: r.content_title,
          หมวดหมู่: r.category_title,
          เปิดเอกสาร: r.document_opens,
          ผู้เข้าชม: r.unique_visitors,
        }))
      );
      XLSX.utils.book_append_sheet(wb, contentSheet, 'ตามเอกสาร');

      const pageSheet = XLSX.utils.json_to_sheet(
        (byPage || []).map((r) => ({
          หน้า: r.page_title || r.page_path,
          Path: r.page_path,
          PageViews: r.page_views,
          ผู้เข้าชม: r.unique_visitors,
        }))
      );
      XLSX.utils.book_append_sheet(wb, pageSheet, 'ตามหน้า');

      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `สถิติการเข้าชม_${periodLabelThai(meta).replace(/\s+/g, '_')}.xlsx`
      );

      toast({ title: 'ส่งออก Excel สำเร็จ', status: 'success', duration: 2500 });
    } catch (err) {
      toast({ title: 'ส่งออก Excel ไม่สำเร็จ', description: err.message, status: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        gap={3}
        mb={5}
      >
        <Box>
          <Heading size="md" color="gray.700">
            สถิติการเข้าชม
          </Heading>
          <Text color="gray.500" fontSize="sm" mt={1}>
            ผู้เข้าชม, Page View และการเปิดเอกสาร แยกตามหมวดหมู่และรายเอกสาร
          </Text>
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          <Button
            colorScheme="green"
            variant="solid"
            size="sm"
            onClick={exportExcel}
            isLoading={isExporting}
          >
            Export Excel
          </Button>
          <Button size="sm" variant="outline" onClick={fetchData} isLoading={isLoading}>
            รีเฟรช
          </Button>
        </HStack>
      </Flex>

      <Box bg="white" p={4} rounded="xl" shadow="sm" mb={5}>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ md: 'end' }}>
          <Box minW="160px">
            <Text fontSize="sm" mb={1} color="gray.600">
              ช่วงรายงาน
            </Text>
            <Select value={period} onChange={(e) => setPeriod(e.target.value)} size="sm">
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Box>

          {(period === 'year' || period === 'month') && (
            <Box minW="120px">
              <Text fontSize="sm" mb={1} color="gray.600">
                ปี
              </Text>
              <Select value={year} onChange={(e) => setYear(e.target.value)} size="sm">
                {availableYears.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          {period === 'month' && (
            <Box minW="140px">
              <Text fontSize="sm" mb={1} color="gray.600">
                เดือน
              </Text>
              <Select value={month} onChange={(e) => setMonth(e.target.value)} size="sm">
                {MONTH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          {period === 'custom' && (
            <>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.600">
                  ตั้งแต่
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.600">
                  ถึง
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Box>
            </>
          )}

          <Badge colorScheme="blue" alignSelf={{ base: 'flex-start', md: 'center' }} px={3} py={1}>
            {periodLabelThai(meta)}
          </Badge>
        </Stack>
      </Box>

      {error && (
        <Alert status="error" mb={4} rounded="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Center py={20}>
          <Spinner size="xl" color="blue.500" />
        </Center>
      ) : (
        <Box>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
            <GridItem>
              <StatCard
                label="ผู้เข้าชม"
                value={summary?.unique_visitors}
                hint="Unique visitors ตาม visitor ID"
                accent="teal.400"
              />
            </GridItem>
            <GridItem>
              <StatCard
                label="Page Views"
                value={summary?.page_views}
                hint="จำนวนครั้งที่เปิดหน้าเว็บ"
                accent="blue.400"
              />
            </GridItem>
            <GridItem>
              <StatCard
                label="เปิดเอกสาร"
                value={summary?.document_opens}
                hint="จำนวนครั้งที่กดเปิดไฟล์"
                accent="orange.400"
              />
            </GridItem>
          </Grid>

          <Box bg="white" p={4} rounded="xl" shadow="sm" mb={4} maxW={{ base: '100%', xl: '55%' }}>
            <Heading size="sm" mb={3} color="gray.700">
              แนวโน้มการเข้าชม
            </Heading>
            {trend.length === 0 ? (
              <Center h="220px">
                <Text color="gray.400">ยังไม่มีข้อมูลในช่วงนี้</Text>
              </Center>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trend} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3182CE" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38B2AC" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#38B2AC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" />
                  <XAxis dataKey="period_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={36} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="page_views"
                    name="Page Views"
                    stroke="#3182CE"
                    fill="url(#colorPv)"
                  />
                  <Area
                    type="monotone"
                    dataKey="unique_visitors"
                    name="ผู้เข้าชม"
                    stroke="#38B2AC"
                    fill="url(#colorUv)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>

          <Box bg="white" p={4} rounded="xl" shadow="sm" mb={6}>
            <Heading size="sm" mb={3} color="gray.700">
              หมวดหมู่ยอดนิยม
            </Heading>
            {categoryChartData.length === 0 ? (
              <Center h="220px">
                <Text color="gray.400">ยังไม่มีข้อมูลหมวดหมู่</Text>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Box minW={`${categoryYAxisWidth + 280}px`}>
                  <ResponsiveContainer width="100%" height={categoryChartHeight}>
                    <BarChart
                      data={categoryChartData}
                      layout="vertical"
                      margin={{ top: 8, left: 8, right: 24, bottom: 8 }}
                      barCategoryGap="24%"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category"
                        dataKey="category_title"
                        width={categoryYAxisWidth}
                        interval={0}
                        tick={{ fontSize: 12, fill: '#4A5568' }}
                      />
                      <Tooltip
                        formatter={(value, name) => [formatNumber(value), name]}
                        labelFormatter={(label) => label}
                      />
                      <Legend />
                      <Bar dataKey="page_views" name="Page Views" fill="#3182CE" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="document_opens" name="เปิดเอกสาร" fill="#ED8936" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </Box>

          <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={4} mb={6}>
            <Box bg="white" p={4} rounded="xl" shadow="sm" overflowX="auto">
              <Heading size="sm" mb={3} color="gray.700">
                สรุปตามหมวดหมู่
              </Heading>
              <Divider mb={3} />
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>หมวดหมู่</Th>
                    <Th isNumeric>ผู้เข้าชม</Th>
                    <Th isNumeric>Page Views</Th>
                    <Th isNumeric>เปิดเอกสาร</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {byCategory.length === 0 ? (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.400" textAlign="center">
                          ไม่มีข้อมูล
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    byCategory.map((row) => (
                      <Tr key={row.category_id}>
                        <Td>{row.category_title}</Td>
                        <Td isNumeric>{formatNumber(row.unique_visitors)}</Td>
                        <Td isNumeric>{formatNumber(row.page_views)}</Td>
                        <Td isNumeric>{formatNumber(row.document_opens)}</Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>

            <Box bg="white" p={4} rounded="xl" shadow="sm" overflowX="auto">
              <Heading size="sm" mb={3} color="gray.700">
                สรุปตามเอกสาร
              </Heading>
              <Divider mb={3} />
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>เอกสาร</Th>
                    <Th>หมวดหมู่</Th>
                    <Th isNumeric>เปิดเอกสาร</Th>
                    <Th isNumeric>ผู้เข้าชม</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {byContent.length === 0 ? (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.400" textAlign="center">
                          ไม่มีข้อมูล
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    byContent.map((row) => (
                      <Tr key={row.content_id}>
                        <Td maxW="220px">
                          <Text noOfLines={2}>{row.content_title}</Text>
                        </Td>
                        <Td>
                          <Text noOfLines={1} fontSize="sm" color="gray.600">
                            {row.category_title}
                          </Text>
                        </Td>
                        <Td isNumeric>{formatNumber(row.document_opens)}</Td>
                        <Td isNumeric>{formatNumber(row.unique_visitors)}</Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Grid>

          <Box bg="white" p={4} rounded="xl" shadow="sm" overflowX="auto">
            <Heading size="sm" mb={3} color="gray.700">
              หน้าเว็บยอดนิยม
            </Heading>
            <Divider mb={3} />
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>หน้า</Th>
                  <Th>Path</Th>
                  <Th isNumeric>Page Views</Th>
                  <Th isNumeric>ผู้เข้าชม</Th>
                </Tr>
              </Thead>
              <Tbody>
                {byPage.length === 0 ? (
                  <Tr>
                    <Td colSpan={4}>
                      <Text color="gray.400" textAlign="center">
                        ไม่มีข้อมูล
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  byPage.map((row) => (
                    <Tr key={row.page_path}>
                      <Td>{row.page_title || '-'}</Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {row.page_path}
                        </Text>
                      </Td>
                      <Td isNumeric>{formatNumber(row.page_views)}</Td>
                      <Td isNumeric>{formatNumber(row.unique_visitors)}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>

          <Text fontSize="xs" color="gray.400" mt={4} textAlign="right">
            ออกรายงานเมื่อ {new Date().toLocaleString('th-TH')} · ช่วง {periodLabelThai(meta)}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default StatisticsDashboard;
